import * as SentryReact from '@sentry/react';
import type { Sentry, SentryScope, CaptureExceptionOptions, CaptureMessageOptions } from './types';

/**
 * Creates a Sentry adapter that wraps sentry/react
 *
 * This adapter bridges the sentry/react SDK to our framework-agnostic
 * Sentry interface, allowing React applications (both full React apps
 * and React components in Ember apps) to use a consistent API.
 *
 */
export function createReactAdapter(): Sentry {
  return {
    captureException(error: unknown, options?: CaptureExceptionOptions): void {
      SentryReact.withScope((scope: SentryReact.Scope) => {
        if (options?.cft) {
          scope.setTag('CFT', options.cft);
        }

        if (options?.tags) {
          scope.setTags(options.tags);
        }

        if (options?.extra) {
          Object.entries(options.extra).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }

        SentryReact.captureException(error, options?.captureContext);
      });
    },

    captureMessage(message: string, options?: CaptureMessageOptions): void {
      SentryReact.withScope((scope: SentryReact.Scope) => {
        if (options?.cft) {
          scope.setTag('CFT', options.cft);
        }

        if (options?.tags) {
          scope.setTags(options.tags);
        }

        if (options?.extra) {
          Object.entries(options.extra).forEach(([key, value]) => {
            scope.setExtra(key, value);
          });
        }

        SentryReact.captureMessage(message, options?.level);
      });
    },

    withScope(callback: (scope: SentryScope) => void): void {
      SentryReact.withScope((sentryScope: SentryReact.Scope) => {
        const adaptedScope: SentryScope = {
          setTag: (key: string, value: string | undefined) => {
            sentryScope.setTag(key, value);
          },
          setTags: (tags: Record<string, string | undefined>) => {
            sentryScope.setTags(tags);
          },
          setContext: (key: string, context: Record<string, unknown> | null) => {
            sentryScope.setContext(key, context);
          },
          setExtra: (key: string, extra: unknown) => {
            sentryScope.setExtra(key, extra);
          },
          setLevel: level => {
            sentryScope.setLevel(level);
          },
        };

        callback(adaptedScope);
      });
    },

    setTagInCurrentScope(tag: string, value: string | undefined): void {
      SentryReact.getCurrentScope().setTag(tag, value);
    },
  };
}

/**
 * Re-export Sentry.init for convenience
 */
export const initSentry = SentryReact.init;
